import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { Typography, Paper, Button } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface CsvData {
  headers: string[];
  data: Record<string, string>[];
}

interface CsvFileUploaderProps {
  onCsvParsed: (data: CsvData) => void;
  onParsingError?: (error: string) => void;
}

const CsvFileUploader: React.FC<CsvFileUploaderProps> = ({ onCsvParsed, onParsingError }) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        onParsingError?.('Tipo de archivo invalido. Por favor sube un archivo CSV.');
        return;
      }

      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            console.error('CSV Parsing errors:', results.errors);
            onParsingError?.(`Error al procesar el CSV: ${results.errors.map(e => e.message).join(', ')}`);
            return;
          }
          if (!results.meta.fields || results.meta.fields.length === 0) {
            onParsingError?.('El CSV no tiene encabezados o esta vacio.');
            return;
          }
          const filteredData = results.data.filter(row => 
            results.meta.fields?.some(header => row[header] !== null && row[header] !== '')
          );

          onCsvParsed({
            headers: results.meta.fields as string[],
            data: filteredData,
          });
        },
        error: (error: Error) => {
          console.error('CSV Parsing critical error:', error);
          onParsingError?.(`Error critico al procesar el CSV: ${error.message}`);
        }
      });
    }
  }, [onCsvParsed, onParsingError]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    noClick: true, // We will use a separate button to open the dialog
    noKeyboard: true,
  });

  return (
    <Paper 
      {...getRootProps()} 
      elevation={0}
      sx={{
        p: 3,
        textAlign: 'center',
        border: `2px dashed ${isDragActive ? 'primary.main' : 'grey.400'}`,
        backgroundColor: isDragActive ? 'action.hover' : 'background.default',
        cursor: 'pointer',
        transition: 'border .24s ease-in-out, background-color .24s ease-in-out',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <input {...getInputProps()} />
      <UploadFileIcon sx={{ fontSize: 60, mb: 2, color: 'grey.600' }} />
      {isDragActive ? (
        <Typography variant="h6">Suelta el archivo CSV aqui...</Typography>
      ) : (
        <Typography variant="h6">Arrastra y suelta un archivo CSV aqui, o haz clic en el boton</Typography>
      )}
      <Button variant="contained" onClick={open} sx={{ mt: 2 }}>
        Seleccionar archivo CSV
      </Button>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        Solo se aceptan archivos *.csv.
      </Typography>
    </Paper>
  );
};

export default CsvFileUploader; 